import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, MessageSquare, Clock } from "lucide-react";
import { apiClient } from "@/lib/auth";
import ArrangeBoard from "@/components/nds/ArrangeBoard";
import { btnGold, field, when, Empty, Loading } from "@/components/nds/windows/finance/parts";
import { SmsRules } from "@/components/nds/windows/sms/SmsRules";
import { SmsPeople } from "@/components/nds/windows/sms/SmsPeople";

const TONE = { Sent: "text-sky-300", Delivered: "text-emerald-300", Failed: "text-red-300" };
const TRIG = { manual: "Manual message", dues_reminder: "Weekly dues reminder", checkin_due: "Check-in due", sitdown_session: "Sit-down session", ncn_notice: "NCN notice", other: "Other" };
export const Card = ({ id, title, Icon, children }) => (
  <section data-testid={`sms-card-${id}`} className="p-4 rounded-md border border-white/10 bg-white/[0.02] space-y-3 h-full">
    <div className="flex items-center gap-2 text-nex-silver font-semibold"><Icon className="h-4 w-4 text-nex-gold" />{title}</div>{children}
  </section>
);

function Row({ x }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`sms-log-row-${x.id}`} className="w-full text-left py-2 grid grid-cols-[1fr_auto] gap-2 hover:bg-white/[0.03]">
        <div className="min-w-0">
          <div className="text-sm text-nex-silver truncate">{x.name} <span className="text-[10px] uppercase text-nex-muted">{x.type}</span> <span className="font-mono text-[11px] text-nex-muted">{x.phone}</span></div>
          <div className="text-xs text-nex-muted truncate">{x.message}</div>
          <div className="text-[10px] text-nex-muted">{when(x.at)} · {TRIG[x.trigger] || x.trigger} · by {x.sent_by}</div>
        </div>
        <div className={`text-xs font-mono ${TONE[x.status] || "text-amber-300"}`} data-testid={`sms-log-status-${x.id}`}>{x.status}{x.reason && x.status !== "Sent" && x.status !== "Delivered" && <div className="text-[10px] text-nex-muted max-w-[180px] whitespace-normal">{x.reason}</div>}</div>
      </button>
      {open && <div data-testid={`sms-log-detail-${x.id}`} className="pb-3 pl-2 space-y-1 text-xs">
        <p className="text-nex-silver whitespace-pre-wrap">{x.message}</p>
        {x.history.map((h, i) => <div key={i} className="text-[11px] text-nex-muted">{when(h.at)} · {h.label}{h.reason ? ` · ${h.reason}` : ""}</div>)}
      </div>}
    </div>
  );
}

function Delivery() {
  const [s, setS] = useState(null);
  useEffect(() => { apiClient.get("/sms/webhooks").then(({ data }) => setS(data)).catch(() => setS(null)); }, []);
  if (!s) return null;
  const connect = () => apiClient.post("/sms/webhooks/connect", { base_url: window.location.origin })
    .then(() => { toast.success("Delivery reports connected"); setS({ ...s, connected: true, base_url: window.location.origin }); })
    .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't connect"));
  return (
    <div data-testid="sms-delivery" className="text-[11px] text-nex-muted flex flex-wrap items-center gap-2">
      {s.connected ? <span className="text-emerald-300">Delivery reports and STOP / START replies come in from SMSGate · {s.base_url}</span>
        : <span>Delivery reports are not connected yet, so texts show Sent only.</span>}
      <button type="button" onClick={connect} data-testid="sms-delivery-connect" className={btnGold}>{s.connected ? "Reconnect" : "Connect delivery reports"}</button>
    </div>
  );
}

function SentLog() {
  const [f, setF] = useState({ q: "", status: "", type: "", trigger: "", date_from: "", date_to: "" });
  const [d, setD] = useState(null);
  const key = JSON.stringify(f);
  useEffect(() => {
    const t = setTimeout(() => apiClient.get("/sms/log", { params: f }).then(({ data }) => setD(data)).catch((e) => setD({ error: e?.response?.data?.detail || "Couldn't load the log" })), 250);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportCsv = () => apiClient.get("/sms/log.csv", { params: f, responseType: "blob" }).then((r) => {
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "sms-log.csv"; a.click(); URL.revokeObjectURL(a.href);
  }).catch(() => toast.error("Couldn't export"));
  const sel = (k, opts) => <select value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`sms-log-filter-${k}`} className={`${field} py-1`}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
  return (
    <Card id="log" title="Sent log" Icon={MessageSquare}>
      <Delivery />
      <div className="flex flex-wrap gap-2">
        <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search name or phone" data-testid="sms-log-search" className={`${field} py-1 flex-1 min-w-[160px]`} />
        {sel("status", [["", "Any status"], ["sent", "Sent"], ["delivered", "Delivered"], ["failed", "Failed"], ["skipped", "Skipped"]])}
        {sel("type", [["", "Anyone"], ["resident", "Residents"], ["staff", "Staff"], ["partner", "Partners"], ["contact", "Contacts"]])}
        {sel("trigger", [["", "Any trigger"], ...Object.entries(TRIG)])}
        <input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="sms-log-from" className={`${field} py-1`} />
        <input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="sms-log-to" className={`${field} py-1`} />
        <button type="button" onClick={exportCsv} data-testid="sms-log-export" className={`${btnGold} inline-flex items-center gap-1`}><Download className="h-3 w-3" /> CSV</button>
      </div>
      {!d ? <Loading testid="sms-log-loading" /> : d.error ? <p className="text-sm text-red-300">{d.error}</p> : d.rows.length
        ? <div className="max-h-[420px] overflow-y-auto" data-testid="sms-log-list">{d.rows.map((x) => <Row key={x.id} x={x} />)}</div>
        : <Empty testid="sms-log-empty">No texts match.</Empty>}
    </Card>
  );
}

/** SMS window: Sent log, Who gets SMS and When to send — three arrangeable cards (one shared order). */
export default function SmsContent() {
  const [me, setMe] = useState(null);
  useEffect(() => { apiClient.get("/sms/me").then(({ data }) => setMe(data)).catch(() => setMe({})); }, []);
  if (!me) return <Loading testid="sms-loading" />;
  const ids = [me.view_log && "log", me.manage && "people", me.manage && "rules"].filter(Boolean);
  if (!ids.length) return <Empty testid="sms-forbidden">You don't have access to SMS.</Empty>;
  return (
    <div className="p-4" data-testid="sms-content">
      <ArrangeBoard board="sms_window" ids={ids} labels={{ log: "Sent log", people: "Who gets SMS", rules: "When to send" }} gridClass="grid-cols-1" label="cards"
        renderTile={(id) => ({ log: <SentLog />, people: <SmsPeople />, rules: <SmsRules canEdit={me.rules} Card={Card} Icon={Clock} /> }[id])} />
    </div>
  );
}
